import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { apiFetch, ApiClientError } from '@/lib/api-client'
import { useAuthStore } from '@/lib/auth-store'
import { Logo } from '@/components/Logo'
import type { SessionUser } from '@/lib/session-types'

interface RegisterResponse {
  accessToken: string
  refreshToken: string
  user: SessionUser
}

export function RegisterPage() {
  const navigate = useNavigate()
  const setSession = useAuthStore((s) => s.setSession)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [artistSlug, setArtistSlug] = useState('smokey-robinson')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const data = await apiFetch<RegisterResponse>('/auth/register', {
        method: 'POST',
        body: { name, email, password, artistSlug },
        skipAuth: true,
      })
      setSession(data)
      navigate('/dashboard', { replace: true })
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message)
      } else {
        setError('Unexpected error. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex h-full items-center justify-center bg-gradient-to-b from-ovation-paper to-white px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>
        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-xl border border-ovation-ink/10 bg-white p-7 shadow-xl shadow-ovation-ink/5"
        >
          <div>
            <h1 className="font-display text-2xl tracking-tight">Create account</h1>
            <p className="mt-1 text-sm text-ovation-muted">
              Join an existing artist tenant.
            </p>
          </div>

          <Field
            id="name"
            label="Full name"
            value={name}
            onChange={setName}
            autoComplete="name"
            required
          />
          <Field
            id="email"
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
            required
          />
          <Field
            id="password"
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            minLength={8}
            required
            hint="At least 8 characters."
          />
          <Field
            id="artistSlug"
            label="Artist tenant slug"
            value={artistSlug}
            onChange={setArtistSlug}
            required
            hint="Ask your admin which artist tenant to join."
          />

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper transition-colors hover:bg-ovation-ink/90 disabled:opacity-50"
          >
            {submitting ? 'Creating…' : 'Create account'}
          </button>

          <div className="text-center text-xs text-ovation-muted">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-ovation-ink hover:underline">
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  )
}

interface FieldProps {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  autoComplete?: string
  required?: boolean
  minLength?: number
  hint?: string
}

function Field(props: FieldProps) {
  return (
    <div>
      <label htmlFor={props.id} className="mb-1 block text-sm font-medium">
        {props.label}
      </label>
      <input
        id={props.id}
        type={props.type ?? 'text'}
        autoComplete={props.autoComplete}
        required={props.required}
        minLength={props.minLength}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
      />
      {props.hint && <p className="mt-1 text-xs text-ovation-muted">{props.hint}</p>}
    </div>
  )
}
